import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const EMPTY_PLAN = { name: "", price: "₹", unit: "/ Seat / Month", suitable: "", featured: true };

const field =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50";
const labelCls = "text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45";

function PlanCard({ plan, onSave, onDelete, isNew }) {
    const [form, setForm] = useState(plan);
    const [busy, setBusy] = useState(false);
    const dirty = JSON.stringify(form) !== JSON.stringify(plan);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const save = async () => {
        if (!form.name.trim() || !form.price.trim() || form.price === "₹") {
            return toast.error("Plan name and price are required");
        }
        setBusy(true);
        try {
            await onSave(form);
            toast.success(isNew ? "Plan added" : "Plan updated");
        } catch (e) {
            toast.error(apiError(e, "Failed to save plan"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={`plan-card-${plan.id || "new"}`} className="rounded-2xl border border-slate-200/80 bg-white p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <label className={labelCls}>Plan Name</label>
                    <input data-testid={`plan-name-${plan.id || "new"}`} value={form.name} onChange={set("name")} className={field} placeholder="e.g. Private Cabin" />
                </div>
                <div>
                    <label className={labelCls}>Price</label>
                    <input data-testid={`plan-price-${plan.id || "new"}`} value={form.price} onChange={set("price")} className={field} placeholder="₹5,000" />
                </div>
                <div>
                    <label className={labelCls}>Unit</label>
                    <input data-testid={`plan-unit-${plan.id || "new"}`} value={form.unit} onChange={set("unit")} className={field} placeholder="/ Seat / Month" />
                </div>
                <div className="flex items-end pb-1">
                    <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ink/70">
                        <input
                            data-testid={`plan-featured-${plan.id || "new"}`}
                            type="checkbox"
                            checked={form.featured}
                            onChange={set("featured")}
                            className="h-4 w-4 accent-[#2B5CAB]"
                        />
                        Highlighted (gradient border)
                    </label>
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls}>Suitable For</label>
                    <textarea
                        data-testid={`plan-suitable-${plan.id || "new"}`}
                        value={form.suitable}
                        onChange={set("suitable")}
                        rows={2}
                        className={`${field} resize-none`}
                        placeholder="Who is this plan for?"
                    />
                </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
                <button
                    data-testid={`plan-save-${plan.id || "new"}`}
                    onClick={save}
                    disabled={busy || (!dirty && !isNew)}
                    className="btn-gradient rounded-full px-6 py-2.5 text-[13px] font-bold text-white disabled:opacity-50"
                >
                    {busy ? "Saving…" : isNew ? "Add Plan" : "Save Changes"}
                </button>
                {onDelete && (
                    <button
                        data-testid={`plan-delete-${plan.id}`}
                        onClick={onDelete}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2.5 text-[13px] font-bold text-ink/60 transition-colors hover:border-red-300 hover:text-red-600"
                    >
                        <Trash2 size={13} strokeWidth={2.5} />
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}

export default function PlansTab() {
    const [plans, setPlans] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/plans");
            setPlans(data.plans);
        } catch (e) {
            toast.error(apiError(e, "Failed to load plans"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const savePlan = async (form, existing) => {
        const payload = { name: form.name, price: form.price, unit: form.unit, suitable: form.suitable, featured: form.featured };
        if (existing) {
            await api.put(`/api/admin/plans/${existing.id}`, payload);
        } else {
            await api.post("/api/admin/plans", payload);
            setAdding(false);
        }
        await load();
    };

    const deletePlan = async (plan) => {
        if (!window.confirm(`Delete "${plan.name}"?`)) return;
        try {
            await api.delete(`/api/admin/plans/${plan.id}`);
            toast.success("Plan deleted");
            await load();
        } catch (e) {
            toast.error(apiError(e, "Failed to delete plan"));
        }
    };

    if (!plans) {
        return (
            <div className="flex justify-center py-20" data-testid="plans-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="plans-tab" className="space-y-5">
            <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-ink/55">Changes go live on the website immediately after saving.</p>
                {!adding && (
                    <button
                        data-testid="add-plan-btn"
                        onClick={() => setAdding(true)}
                        className="btn-gradient inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-bold text-white"
                    >
                        <Plus size={14} strokeWidth={2.6} />
                        Add Plan
                    </button>
                )}
            </div>
            {adding && (
                <PlanCard plan={{ ...EMPTY_PLAN }} isNew onSave={(form) => savePlan(form, null)} onDelete={() => setAdding(false)} />
            )}
            {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} onSave={(form) => savePlan(form, plan)} onDelete={() => deletePlan(plan)} />
            ))}
        </div>
    );
}
